import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, Clock, CheckCircle, Play, ArrowRightLeft, Loader2, XCircle } from 'lucide-react';
import { Card, MiniBar, StallBadge, WarningBadge } from './primitives';
import { useTopology } from '../TopologyContext';
import { useData } from '../DataContext';

// One pending-job row with an inline "Move ▾" control to relocate it to another
// cluster (calls /api/jobs/move; only PENDING jobs reach this card). On success
// it triggers a snapshot refresh so the queue updates immediately.
function PendingJobRow({ job, isMobile, last }) {
  const { CLUSTERS } = useTopology();
  const { refresh } = useData();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const targets = Object.keys(CLUSTERS).filter(c => c !== job.cluster && CLUSTERS[c].role !== 'postproc');

  const move = async (to) => {
    setBusy(true); setErr(null);
    try {
      const res = await axios.post('/api/jobs/move', {
        jobName: job.jobName, fromProfile: job.cluster, toProfile: to,
      });
      if (res.data?.success) { setOpen(false); setTimeout(refresh, 500); }
      else setErr(res.data?.error || 'move failed');
    } catch (e) {
      setErr(e?.response?.data?.error || 'move failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: '6px 0', borderBottom: last ? 'none' : '1px solid #1e293b' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{
          color: '#d1d5db', fontSize: isMobile ? 11 : 12,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: isMobile ? 110 : 180,
        }} title={job.jobName}>{job.jobName}</span>
        <button onClick={() => setOpen(o => !o)} title="Move to another cluster"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
            background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.4)',
            color: '#eab308', fontSize: 9, fontWeight: 700, padding: '2px 6px',
            borderRadius: 6, cursor: 'pointer',
          }}>
          <ArrowRightLeft style={{ width: 10, height: 10 }} /> Move
        </button>
      </div>
      {open && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ color: '#6b7280', fontSize: 10 }}>{busy ? 'moving…' : 'to:'}</span>
          {busy
            ? <Loader2 className="animate-spin" style={{ width: 12, height: 12, color: '#eab308' }} />
            : targets.map(t => (
                <button key={t} onClick={() => move(t)}
                  style={{
                    width: 22, height: 20, borderRadius: 5, cursor: 'pointer',
                    background: `${CLUSTERS[t].color}22`, border: `1px solid ${CLUSTERS[t].color}`,
                    color: CLUSTERS[t].color, fontSize: 11, fontWeight: 800,
                  }} title={`Move to ${CLUSTERS[t].label}`}>{t}</button>
              ))}
        </div>
      )}
      {err && <div style={{ color: '#fca5a5', fontSize: 10, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// ===== Section header used inside each job card to label clusters =====
function ClusterSectionHeader({ clusterId, count, label }) {
  const { CLUSTERS } = useTopology();
  const c = CLUSTERS[clusterId];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 0', marginBottom: 8,
      borderBottom: `1px solid ${c.color}33`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.color }} />
        <span style={{ color: c.color, fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
          {c.label.toUpperCase()}
        </span>
      </div>
      <span style={{ color: '#9ca3af', fontSize: 11 }}>
        {count} {label}
      </span>
    </div>
  );
}

const STAGE_INDEX = {
  'Discretization': 1, 'Decomposition': 2, 'Simulation': 3, 'Post-Processing': 4,
};

// ===== Compact running-job row =====
function RunningJobRow({ job, isMobile, live, onOpenJob }) {
  const clickable = !!onOpenJob;
  const { refresh } = useData();
  const [clearing, setClearing] = useState(false);
  const [clearErr, setClearErr] = useState(null);

  const clearJob = async (e) => {
    e.stopPropagation();
    if (!window.confirm(
      `Clear "${job.jobName}" from cluster ${job.cluster}?\n\n` +
      `This stops the job (kills its solver processes) and removes it from the queue. ` +
      `Use this for a job that has stalled or finished but won't clear itself. This cannot be undone.`
    )) return;
    setClearing(true); setClearErr(null);
    try {
      const res = await axios.post('/api/jobs/clear', { cluster: job.cluster, jobName: job.jobName });
      if (res.data?.success) setTimeout(refresh, 600);
      else setClearErr(res.data?.error || 'clear failed');
    } catch (err) {
      setClearErr(err?.response?.data?.error || 'clear failed');
    } finally { setClearing(false); }
  };

  const el = job.timestamp
    ? Math.floor((new Date() - new Date(job.timestamp.replace('_', 'T'))) / 1000)
    : 0;
  const eta = job.progress > 0 ? Math.floor((el / job.progress) * (100 - job.progress)) : 0;
  const etaStr = eta > 0 ? `${Math.floor(eta / 3600)}h ${Math.floor((eta % 3600) / 60)}m` : '--';

  const stage = live?.currentStage;
  const sub = live?.subStatus;
  const stepIdx = stage ? STAGE_INDEX[stage] : null;

  return (
    <div
      onClick={clickable ? () => onOpenJob(job) : undefined}
      title={clickable ? 'Open this job in the Jobs tab' : undefined}
      style={{
        backgroundColor: '#1e293b', borderRadius: 14,
        padding: isMobile ? 12 : 14, marginBottom: 10,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = '#243244'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = '#1e293b'; } : undefined}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{
          width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: 10,
          background: 'linear-gradient(135deg, #22c55e, #10b981)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Play style={{ width: isMobile ? 14 : 16, height: isMobile ? 14 : 16, color: '#fff' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <p style={{
              color: '#fff', fontWeight: 500, fontSize: isMobile ? 12 : 13,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: isMobile ? 120 : 200,
            }}>{job.jobName}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, flexShrink: 0 }}>
              <span style={{ color: '#22c55e', fontSize: isMobile ? 14 : 16, fontWeight: 'bold' }}>
                {job.progress.toFixed(1)}%
              </span>
              <button onClick={clearJob} disabled={clearing}
                title="Stop &amp; clear this job (for a stalled/finished job that won't clear itself)"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6,
                  border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.12)',
                  color: '#f87171', fontSize: 10, fontWeight: 700, cursor: clearing ? 'default' : 'pointer',
                }}>
                {clearing
                  ? <Loader2 className="animate-spin" style={{ width: 11, height: 11 }} />
                  : <XCircle style={{ width: 11, height: 11 }} />}
                Clear
              </button>
            </div>
          </div>
          {clearErr && <p style={{ color: '#fca5a5', fontSize: 10, marginBottom: 4 }}>{clearErr}</p>}
          {stage && (
            <p style={{
              color: '#22d3ee', fontSize: isMobile ? 10 : 11, marginBottom: 4,
              fontFamily: 'monospace',
            }}>
              ▶ Step {stepIdx ?? '?'}/4 · {stage}{sub ? ` → ${sub}` : ''}
            </p>
          )}
          <p style={{ color: '#6b7280', fontSize: isMobile ? 10 : 11, marginBottom: 6 }}>
            started {job.timestamp?.split('_')[1]?.slice(0, 5) || '--:--'}
          </p>
          {(Number(job.errorCount) > 0 || (job.status === 'RUNNING' && job.lastActivityTs)) && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
              <StallBadge lastActivityTs={job.lastActivityTs} status={job.status} />
              <WarningBadge count={job.errorCount} />
            </div>
          )}
          <MiniBar percent={job.progress} color="#22d3ee" height={5} />
          <p style={{ color: '#22d3ee', fontSize: 10, marginTop: 4, textAlign: 'right' }}>
            ETA: {etaStr}
          </p>
        </div>
      </div>
    </div>
  );
}

// ===== Active simulations card =====
export function RunningJobsCard({ jobs, jobsLive = [], isMobile, onOpenJob }) {
  const { CLUSTERS } = useTopology();
  const byCluster = useMemo(() => {
    const map = {};
    Object.keys(CLUSTERS).forEach(id => { map[id] = []; });
    jobs.forEach(j => { if (map[j.cluster]) map[j.cluster].push(j); });
    Object.values(map).forEach(arr => arr.sort((a, b) => b.progress - a.progress));
    return map;
  }, [jobs, CLUSTERS]);

  // Index live stage info by jobName so each row can look itself up
  const liveByName = useMemo(() => {
    const m = {};
    (jobsLive || []).forEach(l => { if (l.jobName) m[l.jobName] = l; });
    return m;
  }, [jobsLive]);

  const cardHeight = isMobile ? 280 : 460;

  return (
    <Card style={{
      gridRow: isMobile ? 'span 1' : 'span 2',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Activity style={{ width: 16, height: 16, color: '#22d3ee' }} />
        <h3 style={{ color: '#9ca3af', fontSize: 14 }}>Active Simulations</h3>
      </div>
      {!isMobile && (
        <p style={{ color: '#4b5563', fontSize: 12, marginBottom: 16 }}>
          Currently running across both clusters
        </p>
      )}
      <div style={{ marginBottom: isMobile ? 12 : 16 }}>
        <span style={{ fontSize: isMobile ? 36 : 48, fontWeight: 'bold', color: '#fff' }}>
          {jobs.length}
        </span>
        <span style={{ fontSize: isMobile ? 14 : 16, color: '#6b7280', marginLeft: 8 }}>running</span>
      </div>
      <div style={{ flex: 1, maxHeight: cardHeight - 110, overflowY: 'auto', paddingRight: 4 }}>
        {jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#4b5563' }}>
            <Activity style={{ width: 32, height: 32, margin: '0 auto 8px', opacity: 0.3 }} />
            <p>No active simulations</p>
          </div>
        ) : (
          Object.keys(CLUSTERS).map(cid => (
            <div key={cid} style={{ marginBottom: 14 }}>
              <ClusterSectionHeader clusterId={cid} count={byCluster[cid].length} label="running" />
              {byCluster[cid].length === 0 ? (
                <p style={{ color: '#4b5563', fontSize: 11, padding: '4px 0' }}>
                  No active simulations
                </p>
              ) : (
                byCluster[cid].map((job, i) => (
                  <RunningJobRow
                    key={`${cid}-${job.jobName}-${i}`}
                    job={job}
                    isMobile={isMobile}
                    live={liveByName[job.jobName]}
                    onOpenJob={onOpenJob}
                  />
                ))
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

// ===== Pending jobs queue =====
export function PendingJobsCard({ jobs, isMobile }) {
  const { CLUSTERS } = useTopology();
  const byCluster = useMemo(() => {
    const map = {};
    Object.keys(CLUSTERS).forEach(id => { map[id] = []; });
    jobs.forEach(j => { if (map[j.cluster]) map[j.cluster].push(j); });
    return map;
  }, [jobs, CLUSTERS]);

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Clock style={{ width: 16, height: 16, color: '#eab308' }} />
        <h3 style={{ color: '#9ca3af', fontSize: 14 }}>Job Queue</h3>
      </div>
      {!isMobile && (
        <p style={{ color: '#4b5563', fontSize: 12, marginBottom: 16 }}>Waiting to run</p>
      )}
      <div style={{ marginBottom: isMobile ? 12 : 16 }}>
        <span style={{ fontSize: isMobile ? 36 : 48, fontWeight: 'bold', color: '#fff' }}>
          {jobs.length}
        </span>
        <span style={{ fontSize: isMobile ? 14 : 16, color: '#6b7280', marginLeft: 8 }}>pending</span>
      </div>
      <div style={{ maxHeight: isMobile ? 180 : 320, overflowY: 'auto', paddingRight: 4 }}>
        {Object.keys(CLUSTERS).map(cid => {
          const clusterJobs = byCluster[cid];
          const show = clusterJobs.slice(0, isMobile ? 3 : 6);
          const rem = clusterJobs.length - show.length;
          return (
            <div key={cid} style={{ marginBottom: 12 }}>
              <ClusterSectionHeader clusterId={cid} count={clusterJobs.length} label="pending" />
              {clusterJobs.length === 0 ? (
                <p style={{ color: '#4b5563', fontSize: 11, padding: '4px 0' }}>Queue empty</p>
              ) : (
                <>
                  {show.map((j, i) => (
                    <PendingJobRow key={j.jobName || i} job={j} isMobile={isMobile}
                      last={i === show.length - 1} />
                  ))}
                  {rem > 0 && (
                    <p style={{ color: '#6b7280', fontSize: 11, paddingTop: 6 }}>+{rem} more</p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ===== Completed jobs =====
export function CompletedJobsCard({ jobs, isMobile }) {
  const { CLUSTERS } = useTopology();
  const today = new Date().toDateString();
  const todayN = jobs.filter(j =>
    j.completedTime &&
    new Date(j.completedTime.replace('_', 'T')).toDateString() === today
  ).length;

  const byCluster = useMemo(() => {
    const map = {};
    Object.keys(CLUSTERS).forEach(id => { map[id] = []; });
    jobs.forEach(j => { if (map[j.cluster]) map[j.cluster].push(j); });
    return map;
  }, [jobs, CLUSTERS]);

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <CheckCircle style={{ width: 16, height: 16, color: '#22c55e' }} />
        <h3 style={{ color: '#9ca3af', fontSize: 14 }}>Completed</h3>
      </div>
      {!isMobile && (
        <p style={{ color: '#4b5563', fontSize: 12, marginBottom: 16 }}>Finished simulations</p>
      )}
      <div style={{ marginBottom: 4 }}>
        <span style={{ fontSize: isMobile ? 36 : 48, fontWeight: 'bold', color: '#fff' }}>
          {jobs.length}
        </span>
        <span style={{ fontSize: isMobile ? 14 : 16, color: '#6b7280', marginLeft: 8 }}>total</span>
      </div>
      <p style={{
        color: '#22c55e', fontSize: isMobile ? 12 : 14,
        marginBottom: isMobile ? 12 : 16,
      }}>+{todayN} today</p>
      <div style={{ maxHeight: isMobile ? 180 : 320, overflowY: 'auto', paddingRight: 4 }}>
        {Object.keys(CLUSTERS).map(cid => {
          const clusterJobs = byCluster[cid];
          const show = clusterJobs.slice(0, isMobile ? 3 : 6);
          return (
            <div key={cid} style={{ marginBottom: 12 }}>
              <ClusterSectionHeader clusterId={cid} count={clusterJobs.length} label="total" />
              {clusterJobs.length === 0 ? (
                <p style={{ color: '#4b5563', fontSize: 11, padding: '4px 0' }}>
                  None completed yet
                </p>
              ) : (
                show.map((j, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                    borderBottom: i < show.length - 1 ? '1px solid #1e293b' : 'none',
                  }}>
                    <span style={{
                      color: '#d1d5db', fontSize: isMobile ? 11 : 12,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: isMobile ? 110 : 200,
                    }}>{j.jobName}</span>
                    <span style={{
                      color: '#6b7280', fontSize: isMobile ? 10 : 11,
                      flexShrink: 0, marginLeft: 8,
                    }}>{j.completedTime?.split('_')[0]}</span>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
